import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { formattaDataOra } from '../../utils/date';

/* Pannello riservato all'admin nella scheda di un'azienda.
 *
 * Raccoglie in un posto solo le informazioni che dicono se l'azienda e'
 * davvero operativa (email confermata, se ha mai effettuato un accesso, se
 * puo' incassare) e le azioni per sbloccarla. Prima erano tutte invisibili:
 * un'azienda creata e mai entrata era indistinguibile da una attiva.
 */
const PannelloAdminAzienda = ({ azienda, idUtente, onCategoriaCambiata }) => {
  const [categorie, setCategorie] = useState([]);
  const [categoriaScelta, setCategoriaScelta] = useState('');
  const [statoStripe, setStatoStripe] = useState(null);
  const [nuovaPassword, setNuovaPassword] = useState('');
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    api.alberoTipologie()
      .then(({ data }) => setCategorie(Array.isArray(data) ? data : []))
      .catch(() => setCategorie([]));
  }, []);

  useEffect(() => {
    // La categoria attuale arriva come descrizione, non come code:
    // si risale al code confrontando le descrizioni disponibili.
    if (!azienda || categorie.length === 0) return;
    const attuale = categorie.find(
      (c) => c.descrizione === azienda.tipologiaAzienda || c.code === azienda.tipologiaAzienda
    );
    setCategoriaScelta(attuale ? attuale.code : '');
  }, [azienda, categorie]);

  const verificaStripe = useCallback(() => {
    if (!azienda?.idAzienda) return;
    api.statoStripe(azienda.idAzienda)
      .then(({ data }) => setStatoStripe(data))
      .catch(() => setStatoStripe(null));
  }, [azienda]);

  useEffect(() => {
    verificaStripe();
  }, [verificaStripe]);

  const cambiaCategoria = async () => {
    if (!categoriaScelta) return;
    setInCorso(true);
    try {
      await api.assegnaCategoriaAzienda(azienda.idAzienda, categoriaScelta);
      toast.success('Categoria aggiornata');
      if (onCategoriaCambiata) onCategoriaCambiata();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Impossibile cambiare la categoria');
    } finally {
      setInCorso(false);
    }
  };

  const inviaLinkAccesso = async () => {
    setInCorso(true);
    try {
      const { data } = await api.inviaAccesso(idUtente);
      toast.success(data.message || 'Link inviato');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Invio non riuscito');
    } finally {
      setInCorso(false);
    }
  };

  const impostaPassword = async () => {
    if (!nuovaPassword) return;
    setInCorso(true);
    try {
      const { data } = await api.impostaPasswordDaAdmin(idUtente, nuovaPassword);
      toast.success(data.message || 'Password aggiornata');
      setNuovaPassword('');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Impossibile impostare la password');
    } finally {
      setInCorso(false);
    }
  };

  const generaLinkStripe = async () => {
    setInCorso(true);
    try {
      const { data } = await api.linkOnboardingStripe(azienda.idAzienda);
      if (data.linkOnboarding) {
        window.open(data.linkOnboarding, '_blank', 'noopener');
        toast.success('Link di onboarding aperto in una nuova scheda');
      } else {
        toast.error(data.errore || 'Link non disponibile');
      }
    } catch (e) {
      toast.error('Impossibile generare il link di onboarding');
    } finally {
      setInCorso(false);
    }
  };

  if (!azienda) return null;

  return (
    <div className="pannello-admin">

      <section className="pannello-admin__sezione">
        <h3>Stato account</h3>
        <dl className="scheda-dati">
          <div>
            <dt>Email titolare</dt>
            <dd>{azienda.emailTitolare || '—'}</dd>
          </div>
          <div>
            <dt>Email confermata</dt>
            <dd>
              <span className={`stato ${azienda.emailVerificata ? 'stato--ok' : 'stato--attesa'}`}>
                {azienda.emailVerificata ? 'Sì' : 'No'}
              </span>
            </dd>
          </div>
          <div>
            <dt>Ultimo accesso</dt>
            <dd>
              {azienda.maiAcceduto ? (
                <span className="stato stato--attesa">Mai entrato</span>
              ) : (
                formattaDataOra(azienda.ultimoAccesso, 'mai')
              )}
            </dd>
          </div>
          <div>
            <dt>Incassi Stripe</dt>
            <dd>
              <span className={`stato ${statoStripe?.abilitato ? 'stato--ok' : 'stato--attesa'}`}>
                {statoStripe ? (statoStripe.abilitato ? 'Attivi' : 'Non attivi') : '—'}
              </span>
              {statoStripe?.messaggio && (
                <span className="scheda-dati__nota">{statoStripe.messaggio}</span>
              )}
            </dd>
          </div>
        </dl>
      </section>

      <section className="pannello-admin__sezione">
        <h3>Categoria</h3>
        <div className="pannello-admin__riga">
          <select
            value={categoriaScelta}
            onChange={(e) => setCategoriaScelta(e.target.value)}
            aria-label="Categoria dell'azienda"
          >
            <option value="">Seleziona una categoria…</option>
            {categorie.map((c) => (
              <option key={c.code} value={c.code}>{c.descrizione}</option>
            ))}
          </select>
          <button type="button" onClick={cambiaCategoria} disabled={inCorso || !categoriaScelta}>
            Assegna
          </button>
        </div>
        <p className="scheda-dati__nota">
          La categoria non limita i tipi di evento creabili: ogni azienda può crearli tutti.
        </p>
      </section>

      <section className="pannello-admin__sezione">
        <h3>Accesso</h3>
        <div className="pannello-admin__riga">
          <button type="button" onClick={inviaLinkAccesso} disabled={inCorso}>
            Invia link per impostare la password
          </button>
        </div>
        <p className="scheda-dati__nota">
          Modo consigliato: l’esercente sceglie la password da sé e nessuno la conosce.
        </p>

        <div className="pannello-admin__riga">
          <input
            type="text"
            placeholder="Oppure imposta una password (min. 8 caratteri)"
            value={nuovaPassword}
            onChange={(e) => setNuovaPassword(e.target.value)}
            autoComplete="off"
          />
          <button
            type="button"
            onClick={impostaPassword}
            disabled={inCorso || nuovaPassword.length < 8}
          >
            Imposta
          </button>
        </div>
        <p className="scheda-dati__nota">
          Impostandola qui la conoscerai anche tu: l’azione viene registrata nei log
          e conferma automaticamente l’indirizzo email.
        </p>
      </section>

      <section className="pannello-admin__sezione">
        <h3>Pagamenti</h3>
        <div className="pannello-admin__riga">
          <button type="button" onClick={generaLinkStripe} disabled={inCorso}>
            Genera link onboarding Stripe
          </button>
          <button type="button" onClick={verificaStripe} disabled={inCorso}>
            Ricontrolla stato
          </button>
        </div>
        <p className="scheda-dati__nota">
          Finché l’onboarding non è completo, gli incassi restano sulla piattaforma.
        </p>
      </section>

    </div>
  );
};

export default PannelloAdminAzienda;
